import moment from "moment";
import { TimeFrameOption } from "@/common/state/models";
import { TimeAxisType } from "@/gql/types";

export type AggregationTimeFrameOption = TimeFrameOption & {
  id: string;
  name: string;
  aggregated: true;
  aggregation_type: "last_x_months" | "quarter" | "half_year" | "year";
  aggregation_value: string;
  /** Comma-separated month keys (YYYY-MM) for backend to sum */
  aggregation_months: string[];
};

/** Narrow dropdown value when the Aggregated toggle can mix option shapes at compile time */
export function isAggregationTimeFrameOption(
  o: TimeFrameOption & { id: string; name: string }
): o is AggregationTimeFrameOption {
  return (
    "aggregated" in o && (o as AggregationTimeFrameOption).aggregated === true
  );
}

const getOrgStartMonth = (orgStartDate: string) =>
  moment(orgStartDate, "YYYY-MM-DD").startOf("month");

const getMaxAllowedMonthEnd = () => moment().endOf("month");

const isValidBoundedRange = (
  periodStart: moment.Moment,
  periodEnd: moment.Moment,
  orgStartDate: string
) => {
  const orgStart = getOrgStartMonth(orgStartDate);
  const maxAllowed = getMaxAllowedMonthEnd();
  // Keep only full ranges that are entirely within the available timeline.
  return !periodStart.isBefore(orgStart) && !periodEnd.isAfter(maxAllowed);
};

/**
 * Last X months = last month + month before that + month before that...
 * e.g. Last 3 months (today Dec 29) = Nov, Oct, Sep
 */
function getLastXMonthsOptions(
  orgStartDate: string,
  count: 3 | 6 | 12
): AggregationTimeFrameOption | null {
  const today = moment();
  // Start from last complete month (e.g. Nov for Dec 29)
  const lastMonth = moment(today).subtract(1, "month").startOf("month");
  const periodStart = moment(lastMonth).subtract(count - 1, "months");
  const periodEnd = moment(lastMonth).endOf("month");

  if (!isValidBoundedRange(periodStart, periodEnd, orgStartDate)) {
    return null;
  }

  const months: string[] = [];
  for (let i = 0; i < count; i++) {
    const m = moment(lastMonth).subtract(i, "months");
    months.push(m.format("YYYY-MM"));
  }

  const from = periodStart.format("YYYY-MM-DD");
  const displayName =
    count === 3
      ? "Last 3 months"
      : count === 6
      ? "Last 6 months"
      : "Last 12 months";

  return {
    type: TimeAxisType.Month,
    from,
    displayName,
    disable: false,
    id: `last_${count}_months`,
    name: displayName,
    aggregated: true,
    aggregation_type: "last_x_months",
    aggregation_value: String(count),
    aggregation_months: months,
  };
}

/**
 * Q1 = Jan, Feb, Mar | Q2 = Apr, May, Jun | Q3 = Jul, Aug, Sep | Q4 = Oct, Nov, Dec
 */
function getQuarterOption(
  year: number,
  quarter: 1 | 2 | 3 | 4,
  orgStartDate: string
): AggregationTimeFrameOption | null {
  const startMonth = (quarter - 1) * 3;
  const periodStart = moment([year, startMonth, 1]);
  const periodEnd = moment(periodStart).endOf("quarter");

  if (!isValidBoundedRange(periodStart, periodEnd, orgStartDate)) {
    return null;
  }

  const months: string[] = [];
  for (let m = startMonth; m < startMonth + 3; m++) {
    months.push(moment([year, m, 1]).format("YYYY-MM"));
  }

  const displayName = `Q${quarter} ${year}`;
  return {
    type: TimeAxisType.Month,
    from: periodStart.format("YYYY-MM-DD"),
    displayName,
    disable: false,
    id: `q${quarter}_${year}`,
    name: displayName,
    aggregated: true,
    aggregation_type: "quarter",
    aggregation_value: `${year}-Q${quarter}`,
    aggregation_months: months,
  };
}

/**
 * H1 = Jan to Jun | H2 = Jul to Dec
 */
function getHalfYearOption(
  year: number,
  half: 1 | 2,
  orgStartDate: string
): AggregationTimeFrameOption | null {
  const startMonth = half === 1 ? 0 : 6;
  const periodStart = moment([year, startMonth, 1]);
  const periodEnd = moment(periodStart).add(5, "months").endOf("month");

  if (!isValidBoundedRange(periodStart, periodEnd, orgStartDate)) {
    return null;
  }

  const months: string[] = [];
  for (let m = startMonth; m < startMonth + 6; m++) {
    months.push(moment([year, m, 1]).format("YYYY-MM"));
  }

  const displayName = `H${half} ${year}`;
  return {
    type: TimeAxisType.Month,
    from: periodStart.format("YYYY-MM-DD"),
    displayName,
    disable: false,
    id: `h${half}_${year}`,
    name: displayName,
    aggregated: true,
    aggregation_type: "half_year",
    aggregation_value: `${year}-H${half}`,
    aggregation_months: months,
  };
}

/**
 * Full year
 */
function getYearOption(
  year: number,
  orgStartDate: string
): AggregationTimeFrameOption | null {
  const periodStart = moment([year, 0, 1]);
  const periodEnd = moment([year, 11, 31]);

  if (!isValidBoundedRange(periodStart, periodEnd, orgStartDate)) {
    return null;
  }

  const months: string[] = [];
  for (let m = 0; m < 12; m++) {
    months.push(moment([year, m, 1]).format("YYYY-MM"));
  }

  const displayName = String(year);
  return {
    type: TimeAxisType.Month,
    from: periodStart.format("YYYY-MM-DD"),
    displayName,
    disable: false,
    id: `year_${year}`,
    name: displayName,
    aggregated: true,
    aggregation_type: "year",
    aggregation_value: String(year),
    aggregation_months: months,
  };
}

/**
 * Build aggregation time frame options filtered by org start date.
 * Order: Last X months first, then Quarters, Half-years, Full year.
 */
/**
 * Question ids whose deck is only meaningful over one specific period shape.
 *
 * The Quarterly Management Report derives "Q2 2026 · Apr-Jun" from the months
 * it is given and refuses anything that is not a calendar quarter, so offering
 * "Last 3 months" only lets someone build a report that cannot generate. The
 * Monthly deck is a single-month snapshot and takes no aggregation at all.
 * Keep in step with resolveQuarterFromMonths / resolveMonth in the generator.
 */
export const QUARTER_ONLY_QUESTION_IDS = [
  "aggregated.quarterly_management_report",
];
export const SINGLE_MONTH_QUESTION_IDS = [
  "aggregated.monthly_management_report",
];

/**
 * Questions whose section renders a COMPLETE multi-page deck, not a page.
 *
 * The aggregated flow expands one selection into a summary section plus one
 * section per month, which is right for an ordinary question (a summary page
 * then a page per month). These render the entire report each time, so a
 * three-month quarter produced the same 14-page deck four times over — 56
 * pages. They take exactly one section.
 */
export const SELF_CONTAINED_DECK_QUESTION_IDS = [
  "aggregated.monthly_management_report",
  "aggregated.quarterly_management_report",
];

export function isSelfContainedDeck(questionId: string): boolean {
  return SELF_CONTAINED_DECK_QUESTION_IDS.includes(questionId);
}

/**
 * Whether a saved template is one of the management decks.
 *
 * These decks render a whole report from a single section, so they have none
 * of what the Reports tab's template flow is built around: nothing to compose,
 * no sections to add or reorder, and no aggregation to apply. Running them
 * through it produced the bugs this predicate exists to end — a quarter came
 * out as the same pages repeated four times, and the aggregation dropdown
 * offered periods the deck cannot honour.
 *
 * They belong in Executive Reports, which is a fixed set of ready-made
 * reports with only a period to choose. This predicate is what keeps them out
 * of the template picker and puts them there instead.
 */
export function isManagementDeckTemplate(template: {
  sections?: Array<{ id: string }>;
}): boolean {
  return (template.sections || []).some((s) => isSelfContainedDeck(s.id));
}

/** the deck question ids, in the order Executive Reports lists them */
export const MANAGEMENT_DECKS = [
  {
    question_id: "aggregated.monthly_management_report",
    name: "Monthly Management Report",
    subtitle: "Month-by-month view of information risk and labeling",
    period: "month" as const,
  },
  {
    question_id: "aggregated.quarterly_management_report",
    name: "Quarterly Management Report",
    subtitle: "Quarterly roll-up of information risk and labeling",
    period: "quarter" as const,
  },
];

/**
 * Which aggregation shapes a set of report sections can legally use.
 * `none` means the report takes a plain single period, not an aggregation.
 */
export function allowedAggregationTypes(
  sectionIds: string[]
): AggregationTimeFrameOption["aggregation_type"][] | "none" {
  if (sectionIds.some((id) => SINGLE_MONTH_QUESTION_IDS.includes(id))) {
    return "none";
  }
  if (sectionIds.some((id) => QUARTER_ONLY_QUESTION_IDS.includes(id))) {
    return ["quarter"];
  }
  return ["last_x_months", "quarter", "half_year", "year"];
}

export function getAggregationTimeFrameOptions(
  orgStartDate: string,
  /** when given, only these aggregation shapes are offered */
  allowedTypes?: AggregationTimeFrameOption["aggregation_type"][]
): AggregationTimeFrameOption[] {
  const today = moment();
  const currentYear = today.year();
  const orgStartYear = getOrgStartMonth(orgStartDate).year();
  // Limit to current year + 1 year previous (not all the way back to org start)
  const minYear = Math.max(orgStartYear, currentYear - 1);
  const options: AggregationTimeFrameOption[] = [];

  // Last X months
  for (const count of [3, 6, 12] as const) {
    const opt = getLastXMonthsOptions(orgStartDate, count);
    if (opt) options.push(opt);
  }

  // Quarters (current year + 1 year previous)
  for (let year = currentYear; year >= minYear; year--) {
    for (let q = 4; q >= 1; q--) {
      const opt = getQuarterOption(year, q as 1 | 2 | 3 | 4, orgStartDate);
      if (opt) options.push(opt);
    }
  }

  // Half years (current year + 1 year previous)
  for (let year = currentYear; year >= minYear; year--) {
    for (const h of [2, 1] as const) {
      const opt = getHalfYearOption(year, h, orgStartDate);
      if (opt) options.push(opt);
    }
  }

  // Full years (current year + 1 year previous)
  for (let year = currentYear; year >= minYear; year--) {
    const yearOpt = getYearOption(year, orgStartDate);
    if (yearOpt) options.push(yearOpt);
  }

  return allowedTypes
    ? options.filter((o) => allowedTypes.includes(o.aggregation_type))
    : options;
}

/**
 * The sections to send when generating a management deck.
 *
 * Extracted from RegenerateReportModal rather than rewritten, because this is
 * the shape that took several rounds to get right: a self-contained deck sends
 * ONE section carrying the aggregation, never the summary-plus-a-section-per-
 * month expansion the composable reports use. Sending the expansion rendered
 * the whole deck once per month — a quarter came back as the same pages four
 * times over.
 *
 * Both entry points call this, so the Executive Reports card and the regenerate
 * flow cannot drift into producing different payloads for the same deck.
 */
export function buildManagementDeckSections(
  sections: Array<{
    id: string;
    question: string;
    data_source: any;
    application?: any;
    parameters: any;
    parameters_values?: any;
  }>,
  selection: AggregationTimeFrameOption,
  summary_display_name: string
) {
  return sections.map((s) => ({
    data_source: s.data_source,
    id: s.id,
    question: s.question,
    application: s.application,
    parameters: s.parameters,
    parameters_values: s.parameters_values,
    toc_group: s.id,
    toc_group_is_summary: true,
    time: {
      type: "timeframe" as const,
      time_type: selection.type as "Week" | "Month" | "Year",
      time_value: selection.from,
      displayName: summary_display_name,
      aggregated: true,
      aggregation_type: selection.aggregation_type,
      aggregation_value: selection.aggregation_value,
      aggregation_months: selection.aggregation_months,
    },
  }));
}
